import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { API_BASE_URL } from '../config/api';

// Public API (no auth header injected)
export const publicApi = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Authenticated API (injects access token and auto-refresh on 401)
export const authApi = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// File upload API (no Content-Type header, let browser set multipart/form-data)
export const uploadApi = axios.create({
  baseURL: API_BASE_URL,
});

// Request deduplication map to prevent concurrent duplicate requests
const pendingRequests = new Map<string, Promise<any>>();

// Helper to generate request key for deduplication
const getRequestKey = (config: InternalAxiosRequestConfig): string => {
  return `${config.method}:${config.url}:${JSON.stringify(config.params || {})}`;
};

// Request interceptor function to attach access token and handle deduplication
const attachTokenInterceptor = (config: InternalAxiosRequestConfig) => {
  const stored = localStorage.getItem('auth-storage');
  if (stored) {
    try {
      const authData = JSON.parse(stored);
      const token: string | undefined = authData.state?.token;
      if (token && token.trim() !== '') {
        // Verificar que el token tenga el formato correcto (3 partes separadas por puntos)
        const tokenParts = token.split('.');
        if (tokenParts.length === 3) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      }
    } catch (error) {
      // Silently handle parsing errors
    }
  }
  return config;
};

// Apply interceptors to both APIs
authApi.interceptors.request.use(attachTokenInterceptor);
uploadApi.interceptors.request.use(attachTokenInterceptor);

// Response interceptor to handle 401 errors and deduplication
authApi.interceptors.response.use(
  (response) => {
    // Clean up pending request on success
    if (response.config.method?.toUpperCase() === 'GET') {
      const requestKey = getRequestKey(response.config);
      pendingRequests.delete(requestKey);
    }
    return response;
  },
  async (error: AxiosError & { config: any }) => {
    const originalRequest = error.config;

    // Clean up pending request on error
    if (originalRequest?.method?.toUpperCase() === 'GET') {
      const requestKey = getRequestKey(originalRequest);
      pendingRequests.delete(requestKey);
    }

    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        const stored = localStorage.getItem('auth-storage');
        if (stored) {
          const authData = JSON.parse(stored);
          const refreshToken: string | undefined = authData.state?.refreshToken;
          if (refreshToken && refreshToken.trim() !== '') {
            const refreshResponse = await publicApi.post('/auth/refresh', {}, {
              headers: { Authorization: `Bearer ${refreshToken}` },
            });
            const newToken = (refreshResponse.data as any).token_acceso;
            
            if (newToken && newToken.trim() !== '') {
              // Verificar que el nuevo token tenga el formato correcto
              const tokenParts = newToken.split('.');
              if (tokenParts.length === 3) {
                // Update storage
                authData.state.token = newToken;
                localStorage.setItem('auth-storage', JSON.stringify(authData));
                
                // Retry with new token
                originalRequest.headers.Authorization = `Bearer ${newToken}`;
                return axios(originalRequest);
              } else {
                throw new Error('Invalid token format');
              }
            } else {
              throw new Error('Invalid new token');
            }
          } else {
            throw new Error('No refresh token');
          }
        } else {
          throw new Error('No auth storage');
        }
      } catch (refreshError) {
        // If refresh fails, clear auth state and trigger logout
        if (typeof window !== 'undefined') {
          console.error('Token refresh failed, logging out:', refreshError);
          
          // Clear auth storage
          localStorage.removeItem('auth-storage');
          
          // Dispatch storage event to notify other components
          window.dispatchEvent(new Event('storage'));
          
          // Redirect to login only if not already there
          if (!window.location.pathname.includes('/login')) {
            window.location.href = '/login';
          }
        }
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  }
);

// Wrap authApi.get to add deduplication for GET requests
const originalGet = authApi.get.bind(authApi);
authApi.get = function (url: string, config?: any): Promise<any> {
  const requestKey = `GET:${url}:${JSON.stringify(config?.params || {})}`;
  
  // If there's already a pending request, return it
  if (pendingRequests.has(requestKey)) {
    return pendingRequests.get(requestKey)!;
  }
  
  // Create new request promise
  const requestPromise = originalGet(url, config).finally(() => {
    // Clean up after request completes (success or error)
    pendingRequests.delete(requestKey);
  });
  
  // Store the promise
  pendingRequests.set(requestKey, requestPromise);
  
  return requestPromise;
};

// Default export for convenience
const apiClient = authApi;
export default apiClient;
